import {
  Box,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
} from "chart.js";
import MuiButton from "components/UI/MuiButton";
import { IChartView, ITraineeMandays } from "models/app";
import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from "@mui/material/Tooltip";
import { IMediaQuery } from "types/mediaQuery";
import useResponsive from "hooks/useResponsive";
import DialogModal from "../DialogModal";
import { Download, Fullscreen, Minimize } from "@mui/icons-material";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Legend);

interface Props extends IChartView {}

export const RHBarChart3: React.FC<Props> = ({
  drilldownData,
  Id,
  onExport,
  label,
  drilldownLevels,
  isLocalLabel,
  backgroundColor,
  borderColor,
  borderWidth,
  stack,
  totalValues,
  indexAxis,
  maxHeight,
}) => {
  const [openFullScreen, setOpenFullScreen] = useState<boolean>(false);
  const params: IMediaQuery = { query: "up", key: "md" };
  const mdUp = useResponsive(params);
  const [currentLevel, setCurrentLevel] = useState<number>(1);
  const [parents, setParents] = useState<string[]>([]);
  const [_totalValues, setTotalValues] = useState<number>(totalValues!);

  const [anchorElMenu, setAnchorElMenu] = useState<
    null | (EventTarget & HTMLElement)
  >(null);
  const isMenuOpenMenu = Boolean(anchorElMenu);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorElMenu(event.currentTarget);
  const handleMenuCloseMenu = () => setAnchorElMenu(null);

  const handleFullScreen = () => setOpenFullScreen(true);
  const handleCloseFullScreen = () => setOpenFullScreen(false);

  const handleExport = (type: string) => {
    if (type === "xlsx") {
      // use XLSX to export to pdf
      onExport && onExport();
      return;
    }
    const canvas = document.querySelector("#" + Id) as HTMLCanvasElement;
    const url = canvas.toDataURL("image/" + type);
    const a = document.createElement("a");
    a.href = url;
    a.download = "chart." + type;
    a.click();
  };
  const getLabels = (
    data: ITraineeMandays[],
    level: number,
    parents: string[] = []
  ) => {
    const labels: string[] = [];
    data.forEach((item: ITraineeMandays) => {
      if (checkParents(item, level, parents)) {
        labels.push(item["Level" + level]);
      }
    });
    return labels.filter((value, index, self) => self.indexOf(value) === index);
  };

  const getValues = (
    data: ITraineeMandays[],
    level: number,
    parents: string[] = []
  ) => {
    if (parents.length === 0) {
      const labels = getLabels(data, level, []);
      const values: number[] = [];

      labels.forEach((label) => {
        let sum = 0;

        data.forEach((item: ITraineeMandays) => {
          if (
            item["Level" + level] === label &&
            checkParents(item, level, parents)
          ) {
            sum += item.Value;
          }
        });

        values.push(sum);
      });
      return values;
    } else {
      const labels = getLabels(data, level, parents);
      const values: number[] = [];

      labels.forEach((label) => {
        let sum = 0;

        data.forEach((item: ITraineeMandays) => {
          if (
            item["Level" + level] === label &&
            checkParents(item, level, parents)
          ) {
            sum += item.Value;
          }
        });

        values.push(sum);
      });
      return values;
    }
  };

  const checkParents = (
    item: ITraineeMandays,
    level: number,
    parents: string[]
  ) => {
    const parentLength = parents.length;

    for (let i = 1; i <= Math.min(level - 1, parentLength); i++) {
      if (item["Level" + (level - i)] !== parents[parentLength - i]) {
        return false;
      }
    }

    return true;
  };

  const initialChartData = {
    labels: getLabels(drilldownData!, currentLevel),
    datasets: [
      {
        label,
        data: getValues(drilldownData!, currentLevel),
        backgroundColor: backgroundColor || "#6366F1",
        borderColor: borderColor || "#64748B",
        borderWidth: borderWidth || 1,
        stack: stack || "Stack 10",
      },
    ],
  };

  const getChildrenChartData = (_level: number, parents: string[]) => {
    if (_level === 0) return initialChartData;
    setTotalValues(
      getValues(drilldownData!, _level, parents).reduce((a, b) => a + b, 0)
    );
    return {
      labels: getLabels(drilldownData!, _level, [parents[currentLevel - 1]]),
      datasets: [
        {
          label,
          data: getValues(drilldownData!, _level, parents),
          backgroundColor: backgroundColor || "#6366F1",
          borderColor: borderColor || "#64748B",
          borderWidth: borderWidth || 1,
          stack: stack || "Stack 10",
        },
      ],
    };
  };

  const [chartData, setChartData] = useState<any>(initialChartData);
  const [previousChartData, setPreviousChartData] = useState<any>(null);
  const [treeChartData, setTreeChartData] = useState<any[]>([initialChartData]);

  useEffect(() => {
    if (chartData) {
      let sum = 0;
      chartData.datasets[0].data.forEach((item: any) => {
        sum += item;
      });
      setTotalValues(sum);
    }
    // eslint-disable-next-line
  }, [chartData]);
  const handleBarClick = (event: any, elements: any) => {
    if (currentLevel === drilldownLevels) return;
    const currentChartData = chartData;
    let newParents = [...parents, currentChartData.labels[elements[0].index]];
    const newChartChartData = getChildrenChartData(
      currentLevel + 1,
      newParents
    );
    setPreviousChartData(currentChartData);
    setChartData(newChartChartData);
    setCurrentLevel(currentLevel + 1);
    setParents(newParents);
    setTreeChartData([...treeChartData, newChartChartData]);
  };

  const handleBackButtonClick = () => {
    // if (currentLevel === 1) {
    //     setChartData(initialChartData);
    //     setPreviousChartData(null);
    //     setCurrentLevel(1);
    //     setParents([]);
    //     setTreeChartData([initialChartData]);
    //     return;
    // }
    setTimeout(() => {
      setChartData(treeChartData[currentLevel - 2]);
      setPreviousChartData(treeChartData[currentLevel - 2]);
      setCurrentLevel(currentLevel - 1);
      setParents(parents.slice(0, -1));
      setTreeChartData(treeChartData.slice(0, -1));
    }, 100);
  };
  const options = {
    plugins: {
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        titleColor: "#FFFFFF",
      },
      datalabels: {
        display: true,
        color: "white",
      },
    },
    barPercentage: 1,

    maintainAspectRatio: false,
    elements: {
      bar: {
        borderWidth: 5,
        borderRadius: 10,
      },
    },
    onClick: (event: any, elements: any) => {
      handleBarClick(event, elements);
    },
    onHover: (event: any, elements: any) => {},
    responsive: true,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    indexAxis: indexAxis || ("y" as const),
  };

  return (
    <>
      <Grid
        container
        justifyContent={!isLocalLabel ? "space-between" : "end"}
        alignItems="center"
        mb={2}
      >
        {!isLocalLabel && (
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              alignItems: "center",
            }}
          >
            {label + `(${_totalValues})`}
          </Typography>
        )}
        <Stack direction={"row"} justifyContent={"end"} alignItems={"center"}>
          <Tooltip title="Full Screen">
            <IconButton onClick={handleFullScreen}>
              <Fullscreen />
            </IconButton>
          </Tooltip>
          {onExport && (
            <Tooltip title="Download Report">
              <IconButton onClick={onExport}>
                <Download />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Export">
            <IconButton onClick={handleOpenMenu}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Menu
          id={"account-menu1"}
          anchorEl={anchorElMenu}
          open={isMenuOpenMenu}
          onClose={handleMenuCloseMenu}
          transitionDuration={250}
          elevation={0}
          sx={{
            mt: 1,
            "& .MuiPaper-root": {
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
              ".Mui-selected": {
                backgroundColor: "#00B5A2 !important",
                color: "white !important",
                borderRadius: "8px",
              },
            },
          }}
          keepMounted
        >
          <MenuItem onClick={() => handleExport("png")}>Export as PNG</MenuItem>
          <MenuItem onClick={() => handleExport("svg")}>Export as SVG</MenuItem>
          <MenuItem onClick={() => handleExport("jpg")}>Export as JPG</MenuItem>
          <MenuItem onClick={() => handleExport("xlsx")}>
            Export as EXCEL
          </MenuItem>
        </Menu>
      </Grid>
      {previousChartData && currentLevel > 1 && (
        <Grid
          container
          justifyContent={"end"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          {" "}
          <MuiButton variant="text" onClick={handleBackButtonClick}>
            Back
          </MuiButton>
        </Grid>
      )}
      <div
        style={{
          maxHeight: maxHeight || 500,
          maxWidth: "100%",
          marginLeft: "auto",
          marginRight: "auto",
          overflow: "auto",
        }}
      >
        <Bar
          options={options}
          data={chartData}
          width={mdUp ? 1600 : 2000}
          height={mdUp ? 400 : 800}
          id={Id}
        />
      </div>

      {openFullScreen && (
        <DialogModal
          open={openFullScreen}
          onClose={handleCloseFullScreen}
          title={label}
          fullScreen
          children={
            <Box
              sx={{
                overflow: "auto",
              }}
            >
              <Grid
                container
                justifyContent={"end"}
                alignItems="center"
                sx={{ mb: 4 }}
              >
                <Grid item>
                  <Tooltip title="Full Screen">
                    <IconButton onClick={handleCloseFullScreen}>
                      <Minimize />
                    </IconButton>
                  </Tooltip>
                  {onExport && (
                    <Tooltip title="Download Report">
                      <IconButton onClick={onExport}>
                        <Download />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Export">
                    <IconButton onClick={handleOpenMenu}>
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Menu
                  id={"account-menu1"}
                  anchorEl={anchorElMenu}
                  open={isMenuOpenMenu}
                  onClose={handleMenuCloseMenu}
                  transitionDuration={250}
                  elevation={0}
                  sx={{
                    mt: 1,
                    "& .MuiPaper-root": {
                      backgroundColor: "white",
                      borderRadius: "16px",
                      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
                      ".Mui-selected": {
                        backgroundColor: "#00B5A2 !important",
                        color: "white !important",
                        borderRadius: "8px",
                      },
                    },
                  }}
                  keepMounted
                >
                  <MenuItem onClick={() => handleExport("png")}>
                    Export as PNG
                  </MenuItem>
                  <MenuItem onClick={() => handleExport("svg")}>
                    Export as SVG
                  </MenuItem>
                  <MenuItem onClick={() => handleExport("jpg")}>
                    Export as JPG
                  </MenuItem>
                  <MenuItem onClick={() => handleExport("xlsx")}>
                    Export as EXCEL
                  </MenuItem>
                </Menu>
              </Grid>
              {previousChartData && currentLevel > 1 && (
                <Grid
                  container
                  justifyContent={"end"}
                  alignItems="center"
                  sx={{ mb: 4 }}
                >
                  {" "}
                  <MuiButton variant="text" onClick={handleBackButtonClick}>
                    Back
                  </MuiButton>
                </Grid>
              )}
              <Stack
                sx={{
                  width: "100% !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "auto",
                  minWidth: mdUp ? 1600 : 400,
                  maxHeight: mdUp ? 600 : 600,
                  padding: 2,
                }}
              >
                <Bar options={options} data={chartData} id={Id} />
              </Stack>
            </Box>
          }
        />
      )}
    </>
  );
};

export default RHBarChart3;
